import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { faqRepository } from '@/data/faqs/repositories/faq_repository_impl'
import type { Faq } from '@/domain/faqs/entities/faq.entity'
import { GetFaqByIdUseCase } from '@/domain/faqs/usecases/get_faq_by_id.usecase'
import { UpdateFaqUseCase } from '@/domain/faqs/usecases/update_faq.usecase'
import { getAxiosLikeErrorMessage } from '@/services/apiErrors'
import { logError } from '@/utils/logger'
import type { FaqFormErrors } from '../models/faqs_ui_state'

const defaultGetFaqById = new GetFaqByIdUseCase(faqRepository)
const defaultUpdateFaq = new UpdateFaqUseCase(faqRepository)

export interface UseEditFaqViewModelDeps {
  getFaqById?: GetFaqByIdUseCase
  updateFaq?: UpdateFaqUseCase
}

export function useEditFaqViewModel(deps: UseEditFaqViewModelDeps = {}) {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const getFaqById = deps.getFaqById ?? defaultGetFaqById
  const updateFaq = deps.updateFaq ?? defaultUpdateFaq

  const [faq, setFaq] = useState<Faq | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [question, setQuestion] = useState('')
  const [answer, setAnswer] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [errors, setErrors] = useState<FaqFormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const applyFaqToForm = useCallback((item: Faq) => {
    setFaq(item)
    setQuestion(item.question)
    setAnswer(item.answer)
    setIsActive(item.status === 'active')
  }, [])

  useEffect(() => {
    if (!id) {
      setIsLoading(false)
      return
    }

    let cancelled = false

    const load = async () => {
      try {
        setIsLoading(true)
        const item = await getFaqById.execute(id)
        if (!cancelled) {
          applyFaqToForm(item)
        }
      } catch (error: unknown) {
        logError('Failed to load FAQ', error)
        if (!cancelled) {
          setFaq(null)
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [applyFaqToForm, getFaqById, id])

  const submit = useCallback(async () => {
    if (!id) {
      alert('Invalid FAQ ID')
      return
    }

    setIsSubmitting(true)
    const newErrors: FaqFormErrors = {}

    if (!question.trim()) {
      newErrors.question = 'Question is required'
    }
    if (!answer.trim()) {
      newErrors.answer = 'Answer is required'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      setIsSubmitting(false)
      return
    }

    try {
      await updateFaq.execute({
        id,
        question: question.trim(),
        answer: answer.trim(),
        status: isActive ? 'active' : 'inactive',
      })
      navigate('/admin/cms/faq')
    } catch (error: unknown) {
      logError('Failed to update FAQ:', error)
      alert(getAxiosLikeErrorMessage(error, 'Failed to update FAQ. Please try again.'))
    } finally {
      setIsSubmitting(false)
    }
  }, [answer, id, isActive, navigate, question, updateFaq])

  const cancel = useCallback(() => {
    navigate('/admin/cms/faq')
  }, [navigate])

  return {
    faq,
    isLoading,
    form: {
      question,
      answer,
      isActive,
      errors,
      isSubmitting,
    },
    actions: {
      setQuestion,
      setAnswer,
      setIsActive,
      submit,
      cancel,
      clearFieldError: (field: keyof FaqFormErrors) =>
        setErrors(prev => ({ ...prev, [field]: undefined })),
    },
  }
}
